import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Compass, Mountain, Users, Loader2, ArrowRight } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ROUTES } from "@/lib/routes";

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tier, isBetaUser, refreshSubscription } = useSubscription();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');
  
  // Check for feature context from query params (e.g., coming from a locked feature)
  const featureContext = searchParams.get('feature');

  const handleGetStarted = async (tierName: string) => {
    if (!user) {
      navigate(`/auth?plan=${tierName}`);
      return;
    }

    if (tierName === 'scout') {
      // Already free, no action needed
      return;
    }

    if (tierName === 'organizer') {
      toast({
        title: "Coming Soon",
        description: "Organizer tier is not yet available.",
      });
      return;
    }

    // For now, during beta, grant beta access
    setLoadingTier(tierName);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error("Not authenticated");
      }

      const { data, error } = await supabase.functions.invoke('grant-beta-access', {
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
      });

      if (error) throw error;

      toast({
        title: "Welcome!",
        description: "You now have Adventurer access. Enjoy exploring!",
      });

      refreshSubscription();
      navigate('/activity');
    } catch (error) {
      console.error('Enrollment error:', error);
      toast({
        title: "Error",
        description: "Unable to activate access. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingTier(null);
    }
  };

  const isAdventurer = tier === 'adventurer' || isBetaUser;

  const tiers = [
    {
      name: "Scout",
      key: "scout",
      price: "Free",
      period: "",
      description: "Get started exploring. Perfect for new off-roaders learning the ropes.",
      features: [
        "Explore map, trails, and routes",
        "Upload and share GPX runs (public only)",
        "View your own run details and photos",
        "Basic activity feed (Local, Following, Mine)",
        "Basic metrics and badges",
        "Join public and private events"
      ],
      cta: tier === 'scout' && !isBetaUser ? "Current Plan" : "Downgrade",
      variant: "outline" as const,
      icon: <Compass className="w-5 h-5" />,
      popular: false,
      isCurrent: !!user && tier === 'scout' && !isBetaUser,
    },
    {
      name: "Adventurer",
      key: "adventurer",
      price: billingPeriod === 'annual' ? "$29.99" : "$4.99",
      period: billingPeriod === 'annual' ? "/year" : "/month",
      description: "For serious explorers ready to unlock the full trail experience.",
      features: [
        "Everything in Scout",
        "View community run details, photos & waypoints",
        "Trail segment difficulty feedback & sentiment",
        "Control run privacy (public/private)",
        "Edit and clean run tracks",
        "Enhanced dashboard insights",
        "Personalized trail recommendations"
      ],
      cta: isAdventurer ? "Current Plan" : "Get Started",
      variant: "hero" as const,
      icon: <Mountain className="w-5 h-5" />,
      popular: true,
      isCurrent: !!user && isAdventurer,
    },
    {
      name: "Organizer",
      key: "organizer",
      price: "Coming",
      period: "soon",
      description: "Lead the pack. Coordinate rides and build your off-road community.",
      features: [
        "Everything in Adventurer",
        "Create and host events (public or private)",
        "Event waiver form generation",
        "Create and export routes (GPX)",
        "Create and manage clubs",
        "Club-only run sharing",
        "Organizer analytics dashboard",
        "Priority support"
      ],
      cta: tier === 'organizer' ? "Current Plan" : "Coming Soon",
      variant: "adventure" as const,
      icon: <Users className="w-5 h-5" />,
      popular: false,
      badge: "Coming Soon",
      isCurrent: !!user && tier === 'organizer',
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-mountain text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Choose Your
            <span className="text-primary-glow"> Adventure</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Unlock the full trail experience. Track runs, connect with the community, 
            and discover new adventures.
          </p>
          
          {/* Feature context alert */}
          {featureContext && (
            <div className="mt-8 max-w-md mx-auto">
              <Alert className="bg-white/10 border-white/20 text-white">
                <Mountain className="h-4 w-4" />
                <AlertDescription>
                  Upgrade to unlock {featureContext.replace(/-/g, ' ')} and more premium features!
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </section>

      {/* Billing Toggle */}
      <section className="pt-12 pb-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-4">
            <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>
              Monthly
            </span>
            <Switch 
              checked={billingPeriod === 'annual'} 
              onCheckedChange={(checked) => setBillingPeriod(checked ? 'annual' : 'monthly')} 
            />
            <span className={`text-sm font-medium ${billingPeriod === 'annual' ? 'text-foreground' : 'text-muted-foreground'}`}>
              Annual
            </span>
            <Badge variant="secondary" className="ml-2">
              Save 50%
            </Badge>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {tiers.map((tierItem) => (
              <Card key={tierItem.name} className={`relative ${tierItem.popular ? 'ring-2 ring-primary scale-105' : ''} ${tierItem.isCurrent ? 'ring-2 ring-primary' : ''}`}>
                {tierItem.popular && !tierItem.isCurrent && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground px-4 py-1 text-sm font-semibold">
                      Most Popular
                    </Badge>
                  </div>
                )}
                
                {tierItem.isCurrent && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground px-4 py-1 text-sm font-semibold">
                      Your Plan
                    </Badge>
                  </div>
                )}
                
                {tierItem.badge && !tierItem.isCurrent && !tierItem.popular && (
                  <div className="absolute -top-3 right-4">
                    <Badge variant="secondary" className="px-3 py-1 text-xs">
                      {tierItem.badge}
                    </Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-6">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    {tierItem.icon}
                    <CardTitle className="text-2xl">{tierItem.name}</CardTitle>
                  </div>
                  <div className="mb-4">
                    <span className="text-4xl font-bold">
                      {tierItem.price}
                    </span>
                    {tierItem.period && <span className="text-muted-foreground"> {tierItem.period}</span>}
                  </div>
                  <CardDescription className="text-base">
                    {tierItem.description}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3">
                    {tierItem.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-sm leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter>
                  <Button 
                    variant={tierItem.variant} 
                    size="lg" 
                    className="w-full"
                    disabled={tierItem.badge === "Coming Soon" || tierItem.isCurrent || loadingTier === tierItem.key}
                    onClick={() => handleGetStarted(tierItem.key)}
                  >
                    {loadingTier === tierItem.key ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      tierItem.cta
                    )}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold mb-3 text-primary">Why annual billing?</h3>
                <p className="text-muted-foreground">
                  Off-roading is seasonal for many adventurers. Annual billing gives you the 
                  best value — 50% savings compared to monthly.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-primary">Can I cancel anytime?</h3>
                <p className="text-muted-foreground">
                  Yes! You can cancel your subscription at any time. You'll continue to have 
                  access until the end of your billing period.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-primary">What's included in Scout?</h3>
                <p className="text-muted-foreground">
                  Scout is our free tier that lets you explore trails, upload public runs, 
                  and join events. Perfect for getting started.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-primary">When will Organizer be available?</h3>
                <p className="text-muted-foreground">
                  We're working hard on Organizer features! Sign up for Adventurer and 
                  we'll notify you when it launches.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Start Your Adventure?</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Discover new trails, connect with fellow off-roaders, and track your adventures.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!user ? (
              <>
                <Button variant="hero" size="lg" onClick={() => navigate('/auth')}>
                  Get Started
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" size="lg" onClick={() => navigate(ROUTES.explore)}>
                  Explore First
                </Button>
              </>
            ) : !isAdventurer ? (
              <>
                <Button variant="hero" size="lg" onClick={() => handleGetStarted('adventurer')}>
                  <Mountain className="w-4 h-4 mr-2" />
                  Upgrade to Adventurer
                </Button>
                <Button variant="outline" size="lg" onClick={() => navigate(ROUTES.explore)}>
                  Continue Exploring
                </Button>
              </>
            ) : (
              <>
                <Button variant="hero" size="lg" onClick={() => navigate(ROUTES.dashboard)}>
                  Go to Dashboard
                </Button>
                <Button variant="outline" size="lg" onClick={() => navigate(ROUTES.explore)}>
                  Explore Trails
                </Button>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
